/**
 * pma.reorder-calendar.js — anchor-day recurring reorder scheduling (app-proxy backed).
 */
(function () {
  "use strict";

  var API_BASE = "/apps/pma-saved-products/recurring-reorder";
  var WEEKDAYS = [
    { value: 1, label: "Monday" },
    { value: 2, label: "Tuesday" },
    { value: 3, label: "Wednesday" },
    { value: 4, label: "Thursday" },
    { value: 5, label: "Friday" },
  ];

  function parseBlackout(raw) {
    return String(raw || "")
      .split(/[,\n;]+/)
      .map(function (part) {
        return part.trim();
      })
      .filter(Boolean);
  }

  function parseIsoDate(value) {
    if (!value) return null;
    var date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  function formatDisplayDate(date) {
    try {
      return new Intl.DateTimeFormat(undefined, {
        weekday: "short",
        month: "short",
        day: "numeric",
        year: "numeric",
      }).format(date);
    } catch (_err) {
      return date.toISOString().slice(0, 10);
    }
  }

  function isWeekend(date) {
    var day = date.getUTCDay();
    return day === 0 || day === 6;
  }

  function isoDateOnly(date) {
    return date.toISOString().slice(0, 10);
  }

  function shiftToValidBusinessDay(date, blackoutSet) {
    var cursor = new Date(date.getTime());
    for (var i = 0; i < 14; i += 1) {
      var key = isoDateOnly(cursor);
      if (!isWeekend(cursor) && !blackoutSet[key]) return cursor;
      cursor.setUTCDate(cursor.getUTCDate() + 1);
    }
    return date;
  }

  function computeAnchorDate(anchorValue, interval, now) {
    var anchor = Number(anchorValue);
    var base = new Date(now.getTime());
    base.setUTCHours(12, 0, 0, 0);

    if (interval === "MONTH" || interval === "month") {
      var candidate = new Date(Date.UTC(base.getUTCFullYear(), base.getUTCMonth(), Math.min(anchor, 28), 12));
      if (candidate.getTime() - now.getTime() < 48 * 60 * 60 * 1000) {
        candidate.setUTCMonth(candidate.getUTCMonth() + 1);
      }
      return candidate;
    }

    var targetDow = anchor;
    var cursor = new Date(base.getTime());
    for (var i = 0; i < 21; i += 1) {
      var dow = cursor.getUTCDay();
      if (dow === targetDow && cursor.getTime() - now.getTime() >= 48 * 60 * 60 * 1000) {
        return cursor;
      }
      cursor.setUTCDate(cursor.getUTCDate() + 1);
    }
    return new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
  }

  function frequencyLabel(policy) {
    if (!policy) return "Recurring reorder";
    var interval = String(policy.interval || "").toUpperCase();
    var count = Number(policy.intervalCount || 1);
    if (interval === "MONTH") return "Monthly";
    if (interval === "WEEK" && count === 2) return "Every 2 weeks";
    if (interval === "WEEK" || (interval === "DAY" && count === 7)) return "Weekly";
    if (interval === "DAY" && count === 14) return "Every 2 weeks";
    return "Recurring reorder";
  }

  function frequencyKeyFromPolicy(policy) {
    var interval = String((policy && policy.interval) || "").toUpperCase();
    var count = Number((policy && policy.intervalCount) || 1);
    if (interval === "MONTH") return "monthly";
    if (interval === "WEEK" && count === 2) return "biweekly";
    if (interval === "WEEK" || (interval === "DAY" && count === 7)) return "weekly";
    if (interval === "DAY" && count === 14) return "biweekly";
    return "weekly";
  }

  function monthLabel(date) {
    try {
      return new Intl.DateTimeFormat(undefined, { month: "long", year: "numeric" }).format(date);
    } catch (_err) {
      return date.toISOString().slice(0, 7);
    }
  }

  function isBlackoutDay(date, blackoutSet) {
    return !!blackoutSet[isoDateOnly(date)];
  }

  function isValidAnchorCell(date, interval, blackoutSet) {
    if (isBlackoutDay(date, blackoutSet)) return false;
    if (interval === "MONTH") {
      var dom = date.getUTCDate();
      return dom >= 1 && dom <= 28 && !isWeekend(date);
    }
    var dow = date.getUTCDay();
    return dow >= 1 && dow <= 5;
  }

  function renderCalendarMonth(root, state, monthCursor, blackoutSet) {
    var wrap = root.querySelector("[data-pma-rc-calendar-wrap]");
    var body = root.querySelector("[data-pma-rc-calendar-body]");
    var monthEl = root.querySelector("[data-pma-rc-cal-month]");
    if (!wrap || !body || !state.selected) return;

    var policy = state.selected.billingPolicy || state.selected.billing_policy || {};
    var interval = String(policy.interval || "").toUpperCase();
    wrap.hidden = false;
    if (monthEl) monthEl.textContent = monthLabel(monthCursor);

    body.innerHTML = "";
    var year = monthCursor.getUTCFullYear();
    var month = monthCursor.getUTCMonth();
    var first = new Date(Date.UTC(year, month, 1, 12));
    var startOffset = first.getUTCDay();
    var daysInMonth = new Date(Date.UTC(year, month + 1, 0, 12)).getUTCDate();
    var cellIndex = 0;
    var row = document.createElement("tr");
    var minSelectable = Date.now() + 48 * 60 * 60 * 1000;

    for (var pad = 0; pad < startOffset; pad += 1) {
      var emptyCell = document.createElement("td");
      emptyCell.className = "pma-reorder-calendar__cell--empty";
      row.appendChild(emptyCell);
      cellIndex += 1;
    }

    for (var day = 1; day <= daysInMonth; day += 1) {
      if (cellIndex % 7 === 0 && day !== 1) {
        body.appendChild(row);
        row = document.createElement("tr");
      }
      var cellDate = new Date(Date.UTC(year, month, day, 12));
      var cell = document.createElement("td");
      var valid = isValidAnchorCell(cellDate, interval, blackoutSet);
      var isPast = cellDate.getTime() < minSelectable;
      if (isBlackoutDay(cellDate, blackoutSet)) cell.classList.add("pma-reorder-calendar__cell--blackout");
      if (isPast) cell.classList.add("pma-reorder-calendar__cell--past");

      var btn = document.createElement("button");
      btn.type = "button";
      btn.className = "pma-reorder-calendar__day-btn";
      btn.textContent = String(day);
      btn.disabled = !valid || isPast;
      if (valid && !isPast) {
        btn.addEventListener("click", function (pickedDate, pickedDay) {
          return function () {
            var anchorSelect = root.querySelector("[data-pma-rc-anchor]");
            if (!anchorSelect) return;
            if (interval === "MONTH") {
              anchorSelect.value = String(pickedDay);
            } else {
              var jsDow = pickedDate.getUTCDay();
              anchorSelect.value = String(jsDow);
            }
            state.calendarSelectedIso = isoDateOnly(pickedDate);
            renderCalendarMonth(root, state, monthCursor, blackoutSet);
            var previewEvent = new Event("change");
            if (anchorSelect) anchorSelect.dispatchEvent(previewEvent);
          };
        }(cellDate, day));
      }
      if (state.calendarSelectedIso === isoDateOnly(cellDate)) {
        cell.classList.add("pma-reorder-calendar__cell--selected");
      }
      cell.appendChild(btn);
      row.appendChild(cell);
      cellIndex += 1;
    }

    while (cellIndex % 7 !== 0) {
      var filler = document.createElement("td");
      filler.className = "pma-reorder-calendar__cell--empty";
      row.appendChild(filler);
      cellIndex += 1;
    }
    body.appendChild(row);
  }

  function contractDisplayName(contract) {
    return (
      contract.displayName ||
      contract.display_name ||
      contract.productTitle ||
      contract.product_title ||
      frequencyLabel(contract.billingPolicy || contract.billing_policy)
    );
  }

  function contractStatus(contract) {
    return String(contract.status || "").toUpperCase();
  }

  function statusLabel(status) {
    if (status === "PAUSED") return "Paused";
    if (status === "ACTIVE") return "Active";
    return status || "Unknown";
  }

  function isManageableContract(contract) {
    var status = contractStatus(contract);
    return status === "ACTIVE" || status === "PAUSED";
  }

  function showFeedback(root, message, isError) {
    var feedback = root.querySelector("[data-pma-rc-feedback]");
    var msg = root.querySelector("[data-pma-rc-feedback-msg]");
    if (!feedback || !msg) return;
    feedback.hidden = !message;
    feedback.classList.toggle("pma-reorder-calendar__feedback--error", !!isError);
    feedback.classList.toggle("pma-reorder-calendar__feedback--success", !isError);
    msg.textContent = message || "";
  }

  function setBlockedMessage(root, message) {
    var panel = root.querySelector("[data-pma-rc-panel]");
    var messageEl = root.querySelector("[data-pma-rc-blocked-message]");
    if (messageEl && message) messageEl.textContent = message;
    if (panel) panel.hidden = false;
    var app = root.querySelector("[data-pma-rc-app]");
    if (app) {
      app.hidden = true;
      app.setAttribute("aria-hidden", "true");
    }
  }

  function revealApp(root) {
    var panel = root.querySelector("[data-pma-rc-panel]");
    var app = root.querySelector("[data-pma-rc-app]");
    if (panel) panel.hidden = true;
    if (app) {
      app.hidden = false;
      app.removeAttribute("aria-hidden");
    }
  }

  function apiFetch(path, options, attempt) {
    var retryAttempt = attempt || 0;
    var headers = Object.assign({ accept: "application/json" }, (options && options.headers) || {});
    return fetch(API_BASE + path, Object.assign({ credentials: "same-origin", headers: headers }, options || {})).then(function (response) {
      return response.text().then(function (text) {
        var body = null;
        if (text) {
          try {
            body = JSON.parse(text);
          } catch (_parseError) {
            var snippet = String(text || "").trim().slice(0, 160);
            var htmlResponse = snippet.startsWith("<");
            if (htmlResponse && retryAttempt < 2) {
              return new Promise(function (resolve) {
                window.setTimeout(resolve, 1200 * (retryAttempt + 1));
              }).then(function () {
                return apiFetch(path, options, retryAttempt + 1);
              });
            }
            throw new Error(
              htmlResponse
                ? "Schedule service returned an unexpected HTML response."
                : snippet || "Schedule service returned invalid JSON.",
            );
          }
        }
        return { ok: response.ok, status: response.status, body: body };
      });
    });
  }

  function populateAnchors(select, contract) {
    select.innerHTML = "";
    var policy = contract.billingPolicy || contract.billing_policy || {};
    var interval = String(policy.interval || "").toUpperCase();
    if (interval === "MONTH") {
      for (var day = 1; day <= 28; day += 1) {
        var option = document.createElement("option");
        option.value = String(day);
        option.textContent = "Day " + day + " of the month";
        select.appendChild(option);
      }
      return;
    }
    WEEKDAYS.forEach(function (weekday) {
      var opt = document.createElement("option");
      opt.value = String(weekday.value);
      opt.textContent = weekday.label;
      select.appendChild(opt);
    });
  }

  function bumpAnchorUntilAfterCurrent(anchorDate, interval, policy, currentNext, blackoutSet) {
    if (!currentNext) return anchorDate;
    var cursor = new Date(anchorDate.getTime());
    var minAllowed = currentNext.getTime();
    for (var bump = 0; bump < 16 && cursor.getTime() < minAllowed; bump += 1) {
      if (interval === "MONTH") {
        cursor.setUTCMonth(cursor.getUTCMonth() + 1);
      } else {
        cursor.setUTCDate(cursor.getUTCDate() + 7 * (Number(policy.intervalCount) || 1));
      }
      cursor = shiftToValidBusinessDay(cursor, blackoutSet);
    }
    return cursor;
  }

  function sortContractsForUi(contracts) {
    return contracts.slice().sort(function (a, b) {
      var aRank = contractStatus(a) === "ACTIVE" ? 0 : 1;
      var bRank = contractStatus(b) === "ACTIVE" ? 0 : 1;
      if (aRank !== bRank) return aRank - bRank;
      var aNext = parseIsoDate(a.nextBillingDate || a.next_billing_date);
      var bNext = parseIsoDate(b.nextBillingDate || b.next_billing_date);
      return (bNext ? bNext.getTime() : 0) - (aNext ? aNext.getTime() : 0);
    });
  }

  function renderContractOptions(contractSelect, contracts) {
    contractSelect.innerHTML = "";
    contracts.forEach(function (contract, index) {
      var option = document.createElement("option");
      option.value = String(index);
      option.textContent =
        contractDisplayName(contract) +
        " · " +
        statusLabel(contractStatus(contract)) +
        " · next " +
        formatDisplayDate(parseIsoDate(contract.nextBillingDate || contract.next_billing_date) || new Date());
      contractSelect.appendChild(option);
    });
    contractSelect.disabled = false;
  }

  function bootRoot(root) {
    var panel = root.querySelector("[data-pma-rc-panel]");
    if (!panel) return;

    var blackout = parseBlackout(panel.getAttribute("data-pma-rc-blackout"));
    var blackoutSet = {};
    blackout.forEach(function (entry) {
      blackoutSet[entry] = true;
    });

    var contractSelect = root.querySelector("[data-pma-rc-contract]");
    var anchorSelect = root.querySelector("[data-pma-rc-anchor]");
    var frequencySelect = root.querySelector("[data-pma-rc-frequency]");
    var changeFrequencyBtn = root.querySelector("[data-pma-rc-change-frequency]");
    var calendarWrap = root.querySelector("[data-pma-rc-calendar-wrap]");
    var calPrev = root.querySelector("[data-pma-rc-cal-prev]");
    var calNext = root.querySelector("[data-pma-rc-cal-next]");
    var summary = root.querySelector("[data-pma-rc-summary]");
    var preview = root.querySelector("[data-pma-rc-preview]");
    var saveBtn = root.querySelector("[data-pma-rc-save]");
    var skipBtn = root.querySelector("[data-pma-rc-skip]");
    var pauseBtn = root.querySelector("[data-pma-rc-pause]");
    var resumeBtn = root.querySelector("[data-pma-rc-resume]");
    var cancelBtn = root.querySelector("[data-pma-rc-cancel]");
    var statusEl = root.querySelector("[data-pma-rc-status]");
    var scheduleActions = root.querySelector("[data-pma-rc-schedule-actions]");
    var state = { contracts: [], selected: null, calendarMonth: new Date(), calendarSelectedIso: null };

    function setScheduleControlsEnabled(enabled) {
      if (anchorSelect) anchorSelect.disabled = !enabled;
      if (frequencySelect) frequencySelect.disabled = !enabled;
      if (changeFrequencyBtn) changeFrequencyBtn.disabled = !enabled;
      if (saveBtn) saveBtn.disabled = !enabled;
      if (skipBtn) skipBtn.disabled = !enabled;
      if (scheduleActions) scheduleActions.hidden = !enabled;
      if (calendarWrap) calendarWrap.hidden = !enabled;
    }

    function updateLifecycleButtons(status) {
      var isActive = status === "ACTIVE";
      var isPaused = status === "PAUSED";
      if (pauseBtn) {
        pauseBtn.hidden = !isActive;
        pauseBtn.disabled = !isActive;
      }
      if (resumeBtn) {
        resumeBtn.hidden = !isPaused;
        resumeBtn.disabled = !isPaused;
      }
      if (cancelBtn) {
        cancelBtn.disabled = !(isActive || isPaused);
      }
    }

    function updateStatusBadge(status) {
      if (!statusEl) return;
      statusEl.hidden = !status;
      statusEl.textContent = statusLabel(status);
      statusEl.classList.remove("pma-reorder-calendar__status--active", "pma-reorder-calendar__status--paused");
      if (status === "ACTIVE") statusEl.classList.add("pma-reorder-calendar__status--active");
      if (status === "PAUSED") statusEl.classList.add("pma-reorder-calendar__status--paused");
    }

    function selectContract(index) {
      state.selected = state.contracts[index] || null;
      if (!state.selected) return;
      var status = contractStatus(state.selected);
      var nextDate = parseIsoDate(state.selected.nextBillingDate || state.selected.next_billing_date) || new Date();
      if (summary) {
        summary.textContent =
          contractDisplayName(state.selected) +
          " · " +
          frequencyLabel(state.selected.billingPolicy || state.selected.billing_policy) +
          " · next order " +
          formatDisplayDate(nextDate);
      }
      updateStatusBadge(status);
      updateLifecycleButtons(status);
      if (status === "PAUSED") {
        setScheduleControlsEnabled(false);
        if (preview) {
          preview.hidden = false;
          preview.textContent =
            "This recurring reorder is paused. Resume it to update the next order date or skip the next cycle.";
        }
        return;
      }
      populateAnchors(anchorSelect, state.selected);
      if (frequencySelect) {
        frequencySelect.value = frequencyKeyFromPolicy(state.selected.billingPolicy || state.selected.billing_policy);
      }
      state.calendarMonth = parseIsoDate(state.selected.nextBillingDate || state.selected.next_billing_date) || new Date();
      state.calendarSelectedIso = null;
      renderCalendarMonth(root, state, state.calendarMonth, blackoutSet);
      setScheduleControlsEnabled(true);
      updatePreview();
    }

    function reloadContracts() {
      return apiFetch("/contracts")
        .then(function (reload) {
          if (!reload || !reload.body || !Array.isArray(reload.body.contracts)) {
            setBlockedMessage(
              root,
              "Your recurring reorder subscription is no longer active. Contact support if you need help starting a new one.",
            );
            return;
          }
          state.contracts = sortContractsForUi(reload.body.contracts.filter(isManageableContract));
          if (!state.contracts.length) {
            setBlockedMessage(
              root,
              "Your recurring reorder subscription is no longer active. Contact support if you need help starting a new one.",
            );
            return;
          }
          revealApp(root);
          renderContractOptions(contractSelect, state.contracts);
          selectContract(0);
        })
        .catch(function () {
          return null;
        });
    }

    function updatePreview() {
      if (!state.selected || !anchorSelect || !preview) return;
      var policy = state.selected.billingPolicy || state.selected.billing_policy || {};
      var interval = String(policy.interval || "").toUpperCase();
      var anchorDate = computeAnchorDate(anchorSelect.value, interval, new Date());
      anchorDate = shiftToValidBusinessDay(anchorDate, blackoutSet);
      anchorDate = bumpAnchorUntilAfterCurrent(
        anchorDate,
        interval,
        policy,
        parseIsoDate(state.selected.nextBillingDate || state.selected.next_billing_date),
        blackoutSet,
      );
      preview.hidden = false;
      preview.textContent =
        "Your anchor day will change to " +
        formatDisplayDate(anchorDate) +
        " — next order billed on " +
        formatDisplayDate(anchorDate) +
        " instead of " +
        formatDisplayDate(parseIsoDate(state.selected.nextBillingDate || state.selected.next_billing_date) || anchorDate) +
        ".";
    }

    if (anchorSelect) anchorSelect.addEventListener("change", updatePreview);

    if (calPrev) {
      calPrev.addEventListener("click", function () {
        state.calendarMonth = new Date(
          Date.UTC(state.calendarMonth.getUTCFullYear(), state.calendarMonth.getUTCMonth() - 1, 1, 12),
        );
        renderCalendarMonth(root, state, state.calendarMonth, blackoutSet);
      });
    }

    if (calNext) {
      calNext.addEventListener("click", function () {
        state.calendarMonth = new Date(
          Date.UTC(state.calendarMonth.getUTCFullYear(), state.calendarMonth.getUTCMonth() + 1, 1, 12),
        );
        renderCalendarMonth(root, state, state.calendarMonth, blackoutSet);
      });
    }

    if (contractSelect) {
      contractSelect.addEventListener("change", function () {
        var index = Number(contractSelect.value);
        if (Number.isNaN(index)) return;
        selectContract(index);
      });
    }

    if (saveBtn) {
      saveBtn.addEventListener("click", function () {
        if (!state.selected) return;
        var policy = state.selected.billingPolicy || state.selected.billing_policy || {};
        var interval = String(policy.interval || "").toUpperCase();
        var currentNext = parseIsoDate(state.selected.nextBillingDate || state.selected.next_billing_date);
        var anchorDate = shiftToValidBusinessDay(
          computeAnchorDate(anchorSelect.value, interval, new Date()),
          blackoutSet,
        );
        anchorDate = bumpAnchorUntilAfterCurrent(anchorDate, interval, policy, currentNext, blackoutSet);
        var confirmed = window.confirm(
          "Your anchor day will change — next order billed on " +
            formatDisplayDate(anchorDate) +
            " instead of " +
            formatDisplayDate(currentNext || anchorDate) +
            ". Your current cycle completes unchanged.",
        );
        if (!confirmed) return;

        saveBtn.disabled = true;
        apiFetch("/contracts/set-next-billing-date", {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({
            contract_token: state.selected.contractToken || state.selected.contract_token,
            next_billing_date: anchorDate.toISOString(),
            expected_current_next_billing_date:
              state.selected.nextBillingDate || state.selected.next_billing_date,
          }),
        })
          .then(function (result) {
            if (!result.ok || !result.body || result.body.ok === false) {
              throw new Error((result.body && (result.body.error || result.body.message)) || "Schedule update failed.");
            }
            var shiftNote =
              result.body.blackout_adjusted || result.body.blackoutAdjusted
                ? " Next order date adjusted to avoid a blackout day."
                : "";
            showFeedback(root, "Preferred anchor day updated." + shiftNote, false);
            return reloadContracts();
          })
          .catch(function (error) {
            showFeedback(root, error.message || "Schedule update failed.", true);
          })
          .finally(function () {
            saveBtn.disabled = false;
          });
      });
    }

    if (changeFrequencyBtn) {
      changeFrequencyBtn.addEventListener("click", function () {
        if (!state.selected || !frequencySelect) return;
        var nextFrequency = frequencySelect.value;
        var currentFrequency = frequencyKeyFromPolicy(
          state.selected.billingPolicy || state.selected.billing_policy,
        );
        if (nextFrequency === currentFrequency) {
          showFeedback(root, "This subscription is already on that frequency.", false);
          return;
        }
        var label = frequencySelect.selectedOptions[0]
          ? frequencySelect.selectedOptions[0].textContent.trim()
          : nextFrequency;
        if (
          !window.confirm(
            "Change delivery frequency to " +
              label +
              "? The new frequency applies from your next billing date; your current cycle is not shortened.",
          )
        ) {
          return;
        }
        changeFrequencyBtn.disabled = true;
        apiFetch("/contracts/change-frequency", {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({
            contract_token: state.selected.contractToken || state.selected.contract_token,
            target_frequency: nextFrequency,
            anchor_day: anchorSelect ? anchorSelect.value : null,
          }),
        })
          .then(function (result) {
            if (!result.ok || !result.body || result.body.ok === false) {
              throw new Error((result.body && (result.body.error || result.body.message)) || "Frequency change failed.");
            }
            showFeedback(root, "Delivery frequency updated.", false);
            return reloadContracts();
          })
          .catch(function (error) {
            showFeedback(root, error.message || "Frequency change failed.", true);
          })
          .finally(function () {
            changeFrequencyBtn.disabled = false;
          });
      });
    }

    if (skipBtn) {
      skipBtn.addEventListener("click", function () {
        if (!state.selected) return;
        if (!window.confirm("Skip your next recurring reorder cycle? This is a one-time skip.")) return;
        skipBtn.disabled = true;
        apiFetch("/contracts/edit-billing-cycle", {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({
            contract_token: state.selected.contractToken || state.selected.contract_token,
            skip: true,
            cycleIndex: 0,
          }),
        })
          .then(function (result) {
            if (!result.ok || !result.body || result.body.ok === false) {
              throw new Error((result.body && (result.body.error || result.body.message)) || "Skip request failed.");
            }
            mergeContractFromResponse(result.body);
            showFeedback(root, "Next cycle skipped.", false);
            return reloadContracts();
          })
          .catch(function (error) {
            showFeedback(root, error.message || "Skip request failed.", true);
          })
          .finally(function () {
            skipBtn.disabled = false;
          });
      });
    }

    function mergeContractFromResponse(body) {
      if (!body || !state.selected) return;
      var patch = body.contract ? Object.assign({}, body.contract) : {};
      if (body.billingCycle && body.billingCycle.billingAttemptExpectedDate) {
        patch.nextBillingDate = body.billingCycle.billingAttemptExpectedDate;
      }
      if (!Object.keys(patch).length) return;
      var selectedIndex = Number(contractSelect.value) || 0;
      state.selected = Object.assign({}, state.selected, patch);
      if (state.contracts[selectedIndex]) {
        state.contracts[selectedIndex] = Object.assign({}, state.contracts[selectedIndex], patch);
      }
      if (patch.status && !isManageableContract(patch)) {
        setBlockedMessage(
          root,
          "Your recurring reorder subscription is no longer active. Contact support if you need help starting a new one.",
        );
        return;
      }
      selectContract(selectedIndex);
    }

    function lifecycleAction(path, confirmMessage, successMessage) {
      if (!state.selected) return Promise.resolve();
      if (!window.confirm(confirmMessage)) return Promise.resolve();
      return apiFetch(path, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          contract_token: state.selected.contractToken || state.selected.contract_token,
        }),
      })
        .then(function (result) {
          if (!result.ok || !result.body || result.body.ok === false) {
            throw new Error((result.body && (result.body.error || result.body.message)) || "Request failed.");
          }
          mergeContractFromResponse(result.body);
          showFeedback(root, successMessage, false);
          return reloadContracts();
        })
        .catch(function (error) {
          showFeedback(root, error.message || "Request failed.", true);
        });
    }

    if (pauseBtn) {
      pauseBtn.addEventListener("click", function () {
        pauseBtn.disabled = true;
        lifecycleAction(
          "/contracts/pause",
          "Pause this recurring reorder? Billing will stop until you resume it.",
          "Recurring reorder paused.",
        ).finally(function () {
          pauseBtn.disabled = false;
        });
      });
    }

    if (resumeBtn) {
      resumeBtn.addEventListener("click", function () {
        resumeBtn.disabled = true;
        lifecycleAction(
          "/contracts/resume",
          "Resume this recurring reorder? Billing will restart on the next scheduled date.",
          "Recurring reorder resumed.",
        ).finally(function () {
          resumeBtn.disabled = false;
        });
      });
    }

    if (cancelBtn) {
      cancelBtn.addEventListener("click", function () {
        cancelBtn.disabled = true;
        lifecycleAction(
          "/contracts/cancel",
          "Cancel this recurring reorder? This cannot be undone from your account.",
          "Recurring reorder cancelled.",
        ).finally(function () {
          cancelBtn.disabled = false;
        });
      });
    }

    apiFetch("/contracts")
      .then(function (result) {
        if (!result.ok || !result.body || result.body.ok === false) {
          throw new Error((result.body && (result.body.error || result.body.message)) || "Schedule service unavailable.");
        }
        state.contracts = sortContractsForUi((result.body.contracts || []).filter(isManageableContract));
        if (!state.contracts.length) {
          setBlockedMessage(
            root,
            "You do not have an active recurring reorder subscription yet. Browse products with a recurring reorder plan or contact support for help.",
          );
          return;
        }
        revealApp(root);
        renderContractOptions(contractSelect, state.contracts);
        selectContract(0);
      })
      .catch(function (error) {
        setBlockedMessage(
          root,
          error.message ||
            "Self-service schedule changes are temporarily unavailable. Please contact support for help.",
        );
      });
  }

  function boot() {
    document.querySelectorAll("[data-pma-reorder-calendar-root]").forEach(bootRoot);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
})();
